import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { toastApiError } from "@/lib/apiError";

const C = { panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", red: "#F87171" };

const DOC_LABEL = { pitch_deck: "Pitch deck", financial_model: "Financial model", mgmt_accounts: "Mgmt accounts" };
const OUTCOME_STYLE = {
  booked: { label: "Booked a session", bg: "rgba(245,158,11,0.15)", fg: C.orange },
  resumed: { label: "Resumed building", bg: "rgba(123,211,206,0.15)", fg: C.tealSoft },
  awaiting: { label: "Awaiting", bg: "rgba(255,255,255,0.08)", fg: "rgba(255,255,255,0.5)" },
};
const MONTHLY_OUTCOME = {
  logged: { label: "Logged actuals", bg: "rgba(44,197,168,0.15)", fg: "#2CC5A8" },
  built: { label: "Built a model", bg: "rgba(245,158,11,0.15)", fg: C.orange },
  awaiting: { label: "Awaiting", bg: "rgba(255,255,255,0.08)", fg: "rgba(255,255,255,0.5)" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const Tile = ({ label, value, accent }) => (
  <div className="rounded-lg px-4 py-3" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">{label}</div>
    <div className="text-xl font-extrabold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
  </div>
);

export default function NudgesPanel() {
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/nudges");
      setData(data);
    } catch (e) { toastApiError(e, "Could not load nudges"); }
    try {
      const { data: md } = await api.get("/admin/nudges/monthly");
      setMonthly(md);
    } catch (e) { /* monthly section is additive */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const previewScan = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/nudges/run?dry_run=true");
      const n7 = data.sent_day7?.length || 0, n14 = data.sent_day14?.length || 0;
      toast.info(`Next scan would send ${n7} day-7 and ${n14} day-14 email${n7 + n14 === 1 ? "" : "s"}`, {
        description: [...(data.sent_day7 || []), ...(data.sent_day14 || [])]
          .slice(0, 5).map((s) => s.email).join(", ") || "Nobody qualifies right now.",
      });
    } catch (e) { toastApiError(e, "Dry run failed"); }
    setPreviewing(false);
  };

  const s = data?.stats || {};
  return (
    <div className="space-y-5" data-testid="admin-nudges-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-white/50 max-w-xl">
          Founders with unfinished documents get an "analyst offer" email after 14 idle days,
          and a final reminder at day 28. Conversions are counted when they book a session or resume building.
        </p>
        <div className="flex gap-2">
          <button onClick={previewScan} disabled={previewing} data-testid="nudges-preview-scan"
            className="text-xs font-semibold px-3 py-2 rounded-md border border-white/15 text-white/70 hover:text-white transition-colors">
            {previewing ? "Scanning…" : "Preview next scan"}
          </button>
          <button onClick={load} data-testid="nudges-refresh"
            className="text-xs font-semibold px-3 py-2 rounded-md text-white" style={{ backgroundColor: C.teal }}>
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="nudges-stats">
        <Tile label="Founders nudged" value={s.episodes ?? "—"} />
        <Tile label="Email 1 (day-14) sent" value={s.day7_sent ?? "—"} />
        <Tile label="Email 2 (day-28) sent" value={s.day14_sent ?? "—"} />
        <Tile label="Resumed building" value={s.resumed ?? "—"} accent={C.tealSoft} />
        <Tile label="Booked a session" value={s.booked ?? "—"} accent={C.orange} />
        <Tile label="Conversion" value={`${s.conversion_pct ?? 0}%`} accent={C.tealSoft} />
      </div>

      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(123,211,206,0.14)" }}>
        <table className="w-full text-sm" data-testid="nudges-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40" style={{ backgroundColor: C.panel }}>
              <th className="px-4 py-3 font-semibold">Founder</th>
              <th className="px-4 py-3 font-semibold">Document</th>
              <th className="px-4 py-3 font-semibold">Email 1 (day-14)</th>
              <th className="px-4 py-3 font-semibold">Email 2 (day-28)</th>
              <th className="px-4 py-3 font-semibold">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">Loading…</td></tr>
            )}
            {!loading && (data?.nudges || []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40" data-testid="nudges-empty">
                No nudges sent yet — the scanner runs every 6 hours.
              </td></tr>
            )}
            {!loading && (data?.nudges || []).map((n, i) => {
              const o = OUTCOME_STYLE[n.outcome] || OUTCOME_STYLE.awaiting;
              return (
                <tr key={`${n.user_id}-${i}`} className="border-t border-white/5" data-testid={`nudge-row-${i}`}>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{n.name || "—"}</div>
                    <div className="text-white/40 text-xs">{n.email}</div>
                  </td>
                  <td className="px-4 py-3 text-white/70">
                    {DOC_LABEL[n.focus_kind] || n.focus_kind || "—"}
                    {n.company && <div className="text-white/40 text-xs">{n.company}</div>}
                  </td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmt(n.day7_sent_at)}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmt(n.day14_sent_at)}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: o.bg, color: o.fg }} data-testid={`nudge-outcome-${i}`}>
                      {o.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Monthly model check-ins / pitches */}
      <div className="pt-2">
        <div className="text-sm font-extrabold text-white mb-1">Monthly model check-ins</div>
        <p className="text-xs text-white/45 max-w-xl mb-3">
          Once a month: model owners are asked "how are you tracking against your forecast?", and active founders without a model get the "your spreadsheet wasn't built for funders" pitch. Conversion = logged actuals / built a model after the email.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="monthly-nudges-stats">
          <Tile label="Check-ins sent" value={monthly?.stats?.checkin_sent ?? "—"} />
          <Tile label="Pitches sent" value={monthly?.stats?.pitch_sent ?? "—"} />
          <Tile label="Logged actuals" value={monthly?.stats?.checkin_logged ?? "—"} accent={C.tealSoft} />
          <Tile label="Built a model" value={monthly?.stats?.pitch_built ?? "—"} accent={C.orange} />
          <Tile label="Already engaged" value={monthly?.stats?.already_logged ?? "—"} />
          <Tile label="Conversion" value={`${monthly?.stats?.conversion_pct ?? 0}%`} accent={C.tealSoft} />
        </div>
        <div className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(123,211,206,0.14)" }}>
          <table className="w-full text-sm" data-testid="monthly-nudges-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40" style={{ backgroundColor: C.panel }}>
                <th className="px-4 py-3 font-semibold">Founder</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Month</th>
                <th className="px-4 py-3 font-semibold">Sent</th>
                <th className="px-4 py-3 font-semibold">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {(monthly?.rows || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-white/40" data-testid="monthly-nudges-empty">
                  No monthly emails sent yet — the scanner sends one per founder per calendar month.
                </td></tr>
              )}
              {(monthly?.rows || []).map((n, i) => {
                const o = MONTHLY_OUTCOME[n.outcome] || MONTHLY_OUTCOME.awaiting;
                return (
                  <tr key={`${n.user_id}-${n.month}-${i}`} className="border-t border-white/5" data-testid={`monthly-nudge-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{n.name || "—"}</div>
                      <div className="text-white/40 text-xs">{n.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/70">
                      {n.variant === "checkin" ? "Model check-in" : "Model pitch"}
                      {n.company && <div className="text-white/40 text-xs">{n.company}</div>}
                    </td>
                    <td className="px-4 py-3 text-white/60">{n.month}</td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmt(n.sent_at)}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: o.bg, color: o.fg }}>
                        {o.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
